import { scope, stateMap } from '@otto-ec/global-resources/navigation';
import { navigationLog } from '@shared/log.browser.ts';
import type { HistoryCheckpoint, NavigationHistory, Unsubscribe } from '@shared/types/History.ts';

type NavigationScope = Awaited<ReturnType<typeof scope>>;
type NavigationEvent = Parameters<Parameters<NavigationScope['onNavigate']['subscribe']>[0]>[0];
type RestorableState = Partial<HistoryCheckpoint>;

const featureName = 'reptile-tilelist';
const navigationScope = scope(featureName, {
    state: stateMap<HistoryCheckpoint>(),
});

function handleCatch(error: unknown, type: string) {
    navigationLog.error(`Failed to ${type} history`, error);
}

function navigate(getScope: (navigation: NavigationScope) => Promise<boolean>): Promise<void> {
    return navigationScope
        .then(getScope)
        .then(() => undefined)
        .catch((error: unknown) => handleCatch(error, 'navigate'));
}

function isHistoryCheckpoint(state: RestorableState): state is HistoryCheckpoint {
    return typeof state.rule === 'string' && typeof state.page === 'object' && state.page !== null;
}

export function createNavigationHistory(): NavigationHistory {
    let entryRule: string | undefined;

    navigationScope
        .then((navigation) => {
            entryRule = navigation.state().get('entryRule');
        })
        .catch((error: unknown) => handleCatch(error, 'read'));
    return {
        seed(state): Promise<void> {
            navigationLog.debug('Seeding navigation history', state);
            entryRule = state.entryRule;
            return navigate((navigation) => navigation.navigate('', { history: 'replace', state }));
        },
        replace(state): Promise<void> {
            navigationLog.debug('Replacing navigation history checkpoint', state);
            entryRule = state.entryRule;
            return navigate((navigation) => navigation.navigate('', { history: 'replace', state }));
        },
        checkpoint(state, url): Promise<void> {
            navigationLog.debug('Creating navigation history checkpoint', { state, url });
            return navigate((navigation) => navigation.navigate(url, { history: 'push', state }));
        },
        pushUrl(url): Promise<void> {
            navigationLog.debug('Pushing navigation history URL', url);
            return navigate((navigation) => navigation.navigate(url, { history: 'push' }));
        },
        currentEntryRule(fallback): string {
            return entryRule ?? fallback;
        },
        onRestore(handler): Unsubscribe {
            let active = true;
            let unsubscribe: Unsubscribe | null = null;

            navigationScope
                .then((navigation) => {
                    if (!active) return;
                    unsubscribe = navigation.onNavigate.subscribe((event: NavigationEvent) => {
                        if (event.type === 'popstate') {
                            const state = event.to.state.toObject<RestorableState>();
                            if (isHistoryCheckpoint(state)) {
                                navigationLog.debug('Restoring navigation history checkpoint', state);
                                handler(state);
                            } else {
                                navigationLog.debug('Skipping navigation history restore without checkpoint state');
                            }
                        }
                    });
                })
                .catch((error: unknown) => handleCatch(error, 'subscribe'));
            return () => {
                active = false;
                unsubscribe?.();
            };
        },
    };
}
